import { apiFetch } from '@/lib/api'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { TPlatformSecret } from '@/types'

const QUERY_KEY = 'platform_secrets'

export function useSecretsList() {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      const res = await apiFetch('/api/secrets')
      if (!res.ok) throw new Error(`Failed to fetch secrets: ${res.status}`)
      return res.json() as Promise<Omit<TPlatformSecret, 'value'>[]>
    },
  })
}

export function useSecretValue(key: string, enabled = false) {
  return useQuery({
    queryKey: [QUERY_KEY, key],
    queryFn: async () => {
      const res = await apiFetch(`/api/secrets/${encodeURIComponent(key)}`)
      if (!res.ok) throw new Error(`Failed to fetch secret: ${res.status}`)
      return res.json() as Promise<TPlatformSecret>
    },
    enabled,
  })
}

export function useCreateSecret() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { key: string; value: string; description?: string }) => {
      const res = await apiFetch('/api/secrets', {
        method: 'POST',
        body: JSON.stringify(data),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed to create secret' }))
        throw new Error(err.error ?? `Failed to create secret: ${res.status}`)
      }
      return res.json() as Promise<TPlatformSecret>
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useUpdateSecret() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ key, data }: { key: string; data: { value?: string; description?: string } }) => {
      const res = await apiFetch(`/api/secrets/${encodeURIComponent(key)}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed to update secret' }))
        throw new Error(err.error ?? `Failed to update secret: ${res.status}`)
      }
      return res.json() as Promise<TPlatformSecret>
    },
    onSuccess: (_data, { key }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, key] })
    },
  })
}

export function useDeleteSecret() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (key: string) => {
      const res = await apiFetch(`/api/secrets/${encodeURIComponent(key)}`, {
        method: 'DELETE',
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed to delete secret' }))
        throw new Error(err.error ?? `Failed to delete secret: ${res.status}`)
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}
